import { RadioGroup } from "react-migration/components/RadioGroup";
import { useLayerScope } from "../hooks/useLayerScope";
import { SelectionFeature } from "src/js/stores/map/store";
import { useCallback, useEffect } from "react";
import { LayerScopeConfig, ScopeType } from "src/js/stores/site/store";
import { useRadiusInput } from "react-migration/lib/map/useRadiusInput";

interface LayerScopeControlsProps {
  siteId: string;
  layerId: string;
  selection: SelectionFeature;
}

export function LayerScopeControls({ siteId, layerId, selection }: LayerScopeControlsProps) {
  const [scopeConfig, setScopeConfig] = useLayerScope(siteId, layerId);

  const { RadiusInput, currentRadiusInMeters } = useRadiusInput({
    geometry: selection.feature?.geometry ?? null,
    enabled: scopeConfig.scopeType === ScopeType.NEARBY,
    defaultRadius: scopeConfig.radius ?? 100,
  });

  const handleScopeChange = useCallback(
    (value: string) => {
      const newConfig: LayerScopeConfig = {
        scopeType: value as ScopeType,
        radius: value === ScopeType.NEARBY ? scopeConfig.radius ?? 100 : undefined,
      };
      setScopeConfig(newConfig);
    },
    [scopeConfig.radius, setScopeConfig]
  );

  useEffect(() => {
    if (scopeConfig.scopeType === ScopeType.NEARBY && currentRadiusInMeters !== scopeConfig.radius) {
      const newConfig: LayerScopeConfig = {
        scopeType: scopeConfig.scopeType,
        radius: currentRadiusInMeters,
      };
      setScopeConfig(newConfig);
    }
  }, [currentRadiusInMeters, scopeConfig.scopeType, scopeConfig.radius, setScopeConfig]);

  return (
    <div className="flex flex-col gap-2">
      <RadioGroup value={scopeConfig.scopeType} onValueChange={handleScopeChange} className="flex flex-row! gap-2">
        <div className="flex flex-row items-center justify-start gap-1 p-1 flex-nowrap">
          <RadioGroup.Item value={ScopeType.ON_SITE} id={`${layerId}-onsite`}>
            <RadioGroup.Indicator />
          </RadioGroup.Item>
          <label htmlFor={`${layerId}-onsite`}>On Site only</label>
        </div>
        <div className="flex flex-row items-center justify-start gap-1 p-1 flex-nowrap">
          <RadioGroup.Item value={ScopeType.NEARBY} id={`${layerId}-nearby`}>
            <RadioGroup.Indicator />
          </RadioGroup.Item>
          <label htmlFor={`${layerId}-nearby`}>On Site and Nearby</label>
        </div>
      </RadioGroup>

      {scopeConfig.scopeType === ScopeType.NEARBY && <div className="ml-6 max-w-[250px]">{RadiusInput}</div>}
    </div>
  );
}
